import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { LANGUAGES, type Language } from '@/api/types';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const DEFAULT_LANGUAGE: Language = 'fr';

const LANGUAGE_LABELS: Record<Language, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  it: 'Italiano',
  de: 'Deutsch',
};

interface LanguageSelectProps {
  value: Language;
  onChange: (lang: Language) => void;
  id?: string;
  disabled?: boolean;
  withLabel?: boolean;
}

export function LanguageSelect({
  value,
  onChange,
  id,
  disabled,
  withLabel = true,
}: LanguageSelectProps): JSX.Element {
  const { t } = useTranslation();
  const generatedId = React.useId();
  const selectId = id ?? generatedId;
  const trigger = (
    <Select value={value} onValueChange={(v: Language) => onChange(v)} disabled={disabled}>
      <SelectTrigger id={selectId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map((l) => (
          <SelectItem key={l} value={l}>
            {LANGUAGE_LABELS[l]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (!withLabel) return trigger;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={selectId} className="flex items-center gap-1.5">
        <Languages className="h-3.5 w-3.5" />
        {t('chat.language')}
      </Label>
      {trigger}
    </div>
  );
}
